import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components";
import { Chip, IconButton, Input, Option, Select, Spinner, Typography } from "@material-tailwind/react";
import { ArrowDownIcon, ArrowUpIcon, Edit2Icon, UserIcon, XIcon } from "lucide-react";
import moment from "moment";
import { convertToPresentableText } from "../../utils/functions";
import { OPERATIONAL_STATUSES, OPERATIONAL_STATUSES_ENUM } from "../../utils/enums";
import { Link } from "react-router-dom";
import { useGetOffices } from "../../custom-hooks/use-offices";
import { InterpretOperationalStatus } from "../../utils/ui-interpreters";
import OfficeCrudModal from "./crud-modal";


const OfficePage = () => {

    // State variables
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOffice, setSelectedOffice] = useState(null);
    const [sort, setSort] = useState('-createdDate');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [filterStatus, setFilterStatus] = useState(OPERATIONAL_STATUSES.OPERATIONAL);
    const [filterCapacity, setFilterCapacity] = useState('');


    useEffect(() => {
        setIsTyping(true);
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setIsTyping(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const { data: offices, isLoading: isOfficesLoading, isError: isOfficesError } = useGetOffices('all', {
        // _page: page,
        // _limit: limit,
        _sort: sort,
        _search: debouncedSearch,
        status: filterStatus,
        capacity: filterCapacity === 'All' ? undefined : filterCapacity,
        // ...filters
    });

    // Open modal for adding a new office
    const openAddOfficeModal = () => {
        setSelectedOffice(null);
        setModalOpen(true);
    };

    // Open modal for updating an office
    const openUpdateOfficeModal = (office) => {
        setSelectedOffice(office);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setSelectedOffice(null);
    };

    const interpretOccupency = (occupency) => {
        if (!occupency) {
            return <Chip color="green" size="sm" className="text-xs" variant="ghost" value={`Full time`} />;
        }
        if (occupency.isFullyOccupied) {
            return <Chip color="red" size="sm" className="text-xs" variant="ghost" value={`Occupied`} />;
        }
        return <Chip color="blue" size="sm" className="text-xs" variant="ghost" value={`${occupency.occupiedHours} hours`} />;
    }

    const TABLE_COLUMNS = [
        {
            label: 'Office #',
            key: 'code',
            sortable: true,
            sort: sort === "code" ? "-code" : "code"
        },
        {
            label: 'Floor',
            key: 'floor.floorCode',
            sortable: true,
            sort: sort === "floor.floorCode" ? "-floor.floorCode" : "floor.floorCode"
        },
        {
            label: 'Capacity',
            key: 'capacity',
            sortable: true,
            sort: sort === "capacity" ? "-capacity" : "capacity"
        },
        {
            label: 'Availability',
            key: 'availability',
            sortable: false,
            sort: null
        },
        {
            label: 'Curr Assig.',
            key: 'currentAssignment',
            sortable: false,
            sort: null
        },
        {
            label: 'Timestamp',
            key: 'createdDate',
            sortable: true,
            sort: sort === "createdDate" ? "-createdDate" : "createdDate"
        },
    ]

    const availableCapacities = ['All', ...Array.from({ length: 20 }, (_, i) => (i + 1).toString())];

    return (
        <div className={``}>
            <div className="m-5 p-5 border rounded-3xl">
                <PageHeader
                    title="Office Management"
                    isLoading={isOfficesLoading}
                    actionOnClick={openAddOfficeModal}
                    actionType="add"
                    actionText="Add New Office"
                />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-2">
                    <Input
                        label="Search Offices"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        icon={isTyping ? <Spinner className="h-4 w-4" /> : <XIcon className={searchTerm ? "cursor-pointer text-gray-500" : "hidden"} size={16} onClick={() => setSearchTerm('')} />}
                    />
                    <Select
                        label="Filter by Capacity"
                        value={filterCapacity}
                        onChange={(value) => setFilterCapacity(value)}
                    >
                        {
                            availableCapacities.map((capacity, index) => (
                                <Option key={index} value={capacity}>{capacity}</Option>
                            ))
                        }
                    </Select>
                    <Select
                        label="Filter by Status"
                        value={filterStatus}
                        onChange={(value) => setFilterStatus(value)}
                    >
                        {OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                            <Option key={index} value={status}>{convertToPresentableText(status)}</Option>
                        ))}
                    </Select>
                </div>
                <div className="overflow-x-auto">
                    {isOfficesLoading ? (
                        <div className="flex justify-center py-5">
                            <Spinner />
                        </div>
                    ) : isOfficesError ? (
                        <div className="text-center py-5 text-red-500">
                            Error loading offices. Please try again.
                        </div>
                    ) : (
                        <table className="w-full min-w-max table-auto text-left">
                            <thead className="text-sm">
                                <tr className="py-3">
                                    <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Offices ({offices?.length})</th>
                                </tr>
                                <tr className="py-3 capitalize">
                                    {
                                        TABLE_COLUMNS.map((column, index) => (
                                            <th
                                                key={index}
                                                className={"cursor-pointer py-3"}
                                                onClick={() => setSort(column.sortable ? column.sort : sort)}
                                            >
                                                <Typography
                                                    variant="medium"
                                                    color="blue-gray"
                                                    className="flex items-center gap-2 font-bold leading-none opacity-70">
                                                    {column.label} {column.sortable ? (column.sort.startsWith('-') ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />) : ''}
                                                </Typography>
                                            </th>
                                        ))
                                    }

                                </tr>
                            </thead>
                            <tbody>
                                {offices?.map((office, index) => (
                                    <tr
                                        key={index}
                                        className={`border-t hover:bg-gray-50 button rounded-full ${office.status === OPERATIONAL_STATUSES.CLOSED ? 'text-gray-500' : ''}`}>
                                        <td className="py-3 text-sm">
                                            <div className="flex gap-1 font-bold">
                                                <span className="text-xs">{office.number}</span>
                                                <span className="text-xs">({office.code})</span>
                                            </div>
                                            <div className="text-xs">
                                                {InterpretOperationalStatus(office.status, "text")}
                                            </div>
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            <div className="font-bold text-sm">
                                                {office.floor.floorCode}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {office.floor.building.name} - {office.floor.building.branch.name}
                                            </div>
                                        </td>
                                        <td className="font-bold py-3 text-lg">
                                            {office.capacity}
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            {
                                                office.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                                    <div className="flex">
                                                        {interpretOccupency(office.occupency)}
                                                    </div>
                                                ) : (
                                                    <div className="flex">
                                                        {InterpretOperationalStatus(office.status)}
                                                    </div>
                                                )
                                            }
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            {
                                                office.currentBookingUsers && office.currentBookingUsers.length > 0 ? (
                                                    office.currentBookingUsers.map(user => (
                                                        <div className="flex gap-1" key={user._id}>
                                                            <Link className="text-xs text-blue-600 hover:text-orange-600 duration-300 flex items-center gap-1" to={`/customer/${user._id}`} target="_blank">
                                                                <UserIcon size={12} /> {user.name}
                                                            </Link>
                                                        </div>
                                                    ))
                                                ) :
                                                    (
                                                        office.status === OPERATIONAL_STATUSES.OPERATIONAL ? (
                                                            <div className="flex">
                                                                <Chip color="green" size="sm" className="text-xs" variant="ghost" value="Available" />
                                                            </div>
                                                        ) : (
                                                            <div className="flex">
                                                                {InterpretOperationalStatus(office.status)}
                                                            </div>
                                                        )
                                                    )

                                            }
                                        </td>
                                        <td className="font-thin py-3 text-sm">
                                            <div className="flex flex-col">
                                                <div className="text-xs">C: {moment(office.createdDate).format('lll')}</div>
                                                <div className="text-xs">U: {moment(office.lastUpdated).format('lll')}</div>
                                            </div>
                                        </td>
                                        <td className="flex gap-1 py-3">
                                            <IconButton
                                                size="sm"
                                                color="orange"
                                                className="rounded-full"
                                                onClick={() => openUpdateOfficeModal(office)}
                                            >
                                                <Edit2Icon size={12} />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                        </table>
                    )}
                </div>

                <OfficeCrudModal
                    isOpen={modalOpen}
                    onClose={closeModal}
                    office={selectedOffice}
                />
            </div>

        </div>
    );
};

export default OfficePage;
